import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CONFIG from '../config';

// ====================================================================
// ImportOrdersPage                                     v22  Stage S6
// --------------------------------------------------------------------
// A complete replacement for ImportOrders.jsx, which was never
// connected to the application. It had no tab, was never imported by
// App.js, and had never been clicked, so none of its faults had ever
// been seen.
//
// WHAT WAS WRONG IN THE OLD PAGE
//
//  * It called http://localhost:8001 — an address typed in by hand.
//    That works only on the machine the server runs on. On the live
//    site every screen would have been blank and every button dead.
//    This page uses CONFIG.API like every other page.
//
//  * There was no forward picker, and every payment was sent as though
//    it were entirely spot, whatever had really happened.
//
//  * A milestone was marked paid the moment any payment was made
//    against it, however small.
//
// WHAT IS NEW
//
//  * Pay from EEFC. Currency sitting in an EEFC account pays a supplier
//    in the same currency — no conversion and no bank spread. The
//    ledgers offered are the ones at the paying bank holding the
//    order's currency, and if there is none the page says so and offers
//    to create one without leaving the screen.
//
//  * The balance bar. Spot + EEFC must equal the amount paid, and the
//    page shows the difference until it does. The server checks the
//    same rule again on saving, against the balances that are really
//    there at that moment.
//
//  * Part-paying a milestone. Pick a milestone and the amount still
//    open on it is filled in, but it can be changed. The milestone is
//    marked paid only once it is actually covered.
//
//  * Undo. The most recent payment can be reversed and the EEFC
//    currency goes back to the parcels it came from.
//
// NO FORWARD BUCKET — ON PURPOSE
//
//  Paying an import needs a BUY forward. Every contract in the system
//  is a SELL forward for exports and there is no column telling them
//  apart, so offering them here would be worse than offering nothing.
//  Deferred item B6 / F2. Buying forward carries a premium that needs
//  handling of its own.
//
// PCFC PAYMENTS APPEAR HERE BUT ARE NOT EDITED HERE
//
//  The PCFC page already pays import bills, and those payments show in
//  the list marked "via PCFC". They cannot be undone from this page:
//  the loan's drawdown rate was worked out from that conversion line.
//  The Undo button explains this rather than failing.
// ====================================================================

const fmt = (n, dp = 2) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const fmt0 = (n) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const ccyCode   = (c) => (c && c.length === 6 ? c.slice(0, 3) : (c || ''));
const pairLabel = (c) => (c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—'));

// Show what the server actually said, not a generic message.
const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title   === 'string') return d.title;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
};

const CURRENCIES = ['USDINR', 'EURINR', 'GBPINR', 'AEDINR', 'SGDINR', 'AUDINR'];

const PAYMENT_TYPES = [
  { value: 'ADVANCE',      label: 'Advance' },
  { value: 'ON_SHIPMENT',  label: 'On shipment' },
  { value: 'ON_RECEIPT',   label: 'On receipt' },
  { value: 'BALANCE',      label: 'Balance' },
  { value: 'OTHER',        label: 'Other' }
];

const emptyOrder = {
  sellerCode: '', orderNo: '', orderDate: '', currency: 'USDINR',
  fcAmount: '', costRate: '', expectedPaymentDate: '', bank: '', notes: ''
};

const emptyMilestone = { milestoneName: '', fcAmount: '', expectedDate: '' };

const emptyPay = {
  paymentDate: '', paymentType: 'ADVANCE', bank: '', scheduleId: '',
  fcAmount: '', spotFc: '', spotRate: '', eefcFc: '', eefcAccountId: '',
  notes: ''
};

const statusPill = (s) => {
  if (s === 'OPEN')      return { background: '#E2EFDA', color: '#375623' };
  if (s === 'PART_PAID') return { background: '#FFF2CC', color: '#7D6608' };
  if (s === 'CLOSED')    return { background: '#DEEAF1', color: '#1F4E79' };
  if (s === 'CANCELLED') return { background: '#FCE4D6', color: '#C00000' };
  return { background: '#F1F5F9', color: '#334155' };
};

const milestonePill = (s) => {
  if (s === 'PAID')      return { background: '#E2EFDA', color: '#375623' };
  if (s === 'PART_PAID') return { background: '#FFF2CC', color: '#7D6608' };
  return { background: '#F1F5F9', color: '#64748B' };
};

const pill = (styleObj, text) => (
  <span style={{
    ...styleObj, borderRadius: '99px', padding: '2px 10px',
    fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap'
  }}>{text}</span>
);

function ImportOrdersPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  // ---- lists ------------------------------------------------------
  const [orders,  setOrders]  = useState([]);
  const [sellers, setSellers] = useState([]);
  const [banks,   setBanks]   = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- filters ----------------------------------------------------
  const [filterStatus,     setFilterStatus]     = useState('');
  const [filterSeller,     setFilterSeller]     = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  // ---- the order being looked at ----------------------------------
  const [selected,   setSelected]   = useState(null);
  const [schedule,   setSchedule]   = useState({ list: [], locked: false, reason: null });
  const [payments,   setPayments]   = useState({ list: [], totals: null, order: null });

  // ---- the order form ---------------------------------------------
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [editingOrder,  setEditingOrder]  = useState(null);
  const [orderForm,     setOrderForm]     = useState(emptyOrder);
  const [milestones,    setMilestones]    = useState([{ ...emptyMilestone }]);
  const [savingOrder,   setSavingOrder]   = useState(false);

  // ---- the payment form -------------------------------------------
  const [showPayForm,    setShowPayForm]    = useState(false);
  const [payForm,        setPayForm]        = useState(emptyPay);
  const [eefcContext,    setEefcContext]    = useState(null);
  const [savingPay,      setSavingPay]      = useState(false);
  const [creatingLedger, setCreatingLedger] = useState(false);

  const [msg, setMsg] = useState({ text: '', kind: 'ok' });

  const say = (text, kind = 'ok') => {
    setMsg({ text, kind });
    if (kind === 'ok') setTimeout(() => setMsg({ text: '', kind: 'ok' }), 4000);
  };

  // ==================================================================
  // Loading
  // ==================================================================
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (filterStatus)     params.push(`status=${encodeURIComponent(filterStatus)}`);
      if (filterSeller)     params.push(`sellerCode=${encodeURIComponent(filterSeller)}`);
      if (includeCancelled) params.push('includeCancelled=true');

      const qs  = params.length ? `?${params.join('&')}` : '';
      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders${qs}`, auth);

      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      say(serverMessage(err, 'The import orders could not be loaded.'), 'err');
      setOrders([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterSeller, includeCancelled, token]);

  const loadSellers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders/sellers`, auth);
      setSellers(Array.isArray(res.data) ? res.data : []);
    } catch { /* the type-ahead is optional */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadBanks = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks`, auth);
      setBanks(Array.isArray(res.data) ? res.data : []);
    } catch { /* the bank list is not fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { loadSellers(); loadBanks(); }, [loadSellers, loadBanks]);

  const loadSchedule = async (id) => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders/${id}/schedule`, auth);

      // Older builds returned a bare array; both shapes are accepted.
      if (Array.isArray(res.data)) {
        setSchedule({ list: res.data, locked: false, reason: null });
      } else {
        setSchedule({
          list:   res.data?.schedule || [],
          locked: !!res.data?.editLocked,
          reason: res.data?.lockReason || null
        });
      }
    } catch {
      setSchedule({ list: [], locked: false, reason: null });
    }
  };

  const loadPayments = async (id) => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders/${id}/payments`, auth);

      setPayments({
        list:   res.data?.payments || [],
        totals: res.data?.totals   || null,
        order:  res.data?.order    || null
      });
    } catch {
      setPayments({ list: [], totals: null, order: null });
    }
  };

  // ------------------------------------------------------------------
  // Which EEFC ledgers can pay this order, and at what rate.
  // Reloaded whenever the bank or the date changes, because both
  // decide the answer.
  // ------------------------------------------------------------------
  const loadEefcContext = async (orderId, bank, date) => {
    if (!orderId) { setEefcContext(null); return; }
    try {
      const params = [];
      if (bank) params.push(`bank=${encodeURIComponent(bank)}`);
      if (date) params.push(`date=${encodeURIComponent(date)}`);
      const qs = params.length ? `?${params.join('&')}` : '';

      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders/${orderId}/eefc-context${qs}`, auth);
      setEefcContext(res.data);
    } catch (err) {
      setEefcContext({
        accounts: [], reason: serverMessage(err, 'The EEFC accounts could not be read.'),
        rate: 0, rateDate: '', rateReason: null
      });
    }
  };

  const refreshSelected = async (id) => {
    await Promise.all([loadSchedule(id), loadPayments(id)]);
    await loadOrders();
  };

  // ==================================================================
  // Opening an order
  // ==================================================================
  const openOrder = async (o) => {
    if (selected?.importOrderId === o.importOrderId) {
      setSelected(null);
      setShowPayForm(false);
      return;
    }
    setSelected(o);
    setShowPayForm(false);
    setEefcContext(null);
    await refreshSelected(o.importOrderId);
  };

  // ==================================================================
  // The order form
  // ==================================================================
  const openNewOrder = () => {
    setOrderForm(emptyOrder);
    setMilestones([{ ...emptyMilestone }]);
    setEditingOrder(null);
    setShowOrderForm(true);
  };

  const openEditOrder = async (o) => {
    setOrderForm({
      sellerCode:          o.sellerCode || '',
      orderNo:             o.orderNo || '',
      orderDate:           (o.orderDate || '').substring(0, 10),
      currency:            o.currency || 'USDINR',
      fcAmount:            o.fcAmount ?? '',
      costRate:            o.costRate ?? '',
      expectedPaymentDate: (o.expectedPaymentDate || '').substring(0, 10),
      bank:                o.bank || '',
      notes:               o.notes || ''
    });
    setEditingOrder(o);

    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/import-orders/${o.importOrderId}/schedule`, auth);
      const list = Array.isArray(res.data) ? res.data : (res.data?.schedule || []);

      setMilestones(list.length
        ? list.map(s => ({
            milestoneName: s.milestoneName || '',
            fcAmount:      s.fcAmount ?? '',
            expectedDate:  (s.expectedDate || '').substring(0, 10)
          }))
        : [{ ...emptyMilestone }]);
    } catch {
      setMilestones([{ ...emptyMilestone }]);
    }

    setShowOrderForm(true);
  };

  const closeOrderForm = () => {
    setShowOrderForm(false);
    setEditingOrder(null);
    setOrderForm(emptyOrder);
    setMilestones([{ ...emptyMilestone }]);
  };

  const changeOrder = (e) => {
    const { name, value } = e.target;
    setOrderForm(f => ({ ...f, [name]: value }));
  };

  const addMilestone    = () => setMilestones(m => [...m, { ...emptyMilestone }]);
  const removeMilestone = (i) => setMilestones(m => m.filter((_, x) => x !== i));
  const changeMilestone = (i, field, value) =>
    setMilestones(m => m.map((row, x) => (x === i ? { ...row, [field]: value } : row)));

  const scheduledTotal = milestones.reduce((s, m) => s + num(m.fcAmount), 0);

  const saveOrder = async () => {
    if (!orderForm.sellerCode.trim()) return say('Enter the seller.', 'err');
    if (!orderForm.orderNo.trim())    return say('Enter the order number.', 'err');
    if (!orderForm.orderDate)         return say('Enter the order date.', 'err');
    if (!orderForm.currency)          return say('Choose the currency.', 'err');
    if (num(orderForm.fcAmount) <= 0) return say('The order amount must be more than zero.', 'err');
    if (num(orderForm.costRate) <= 0)
      return say('Enter the cost rate. Every gain or loss on this order is measured '
               + 'from it.', 'err');

    const valid = milestones.filter(m => m.milestoneName.trim() && num(m.fcAmount) > 0);

    if (valid.length && scheduledTotal > num(orderForm.fcAmount) + 0.01)
      return say(`The milestones add up to ${fmt(scheduledTotal)}, which is more than `
               + `the order itself (${fmt(num(orderForm.fcAmount))}).`, 'err');

    const payload = {
      sellerCode:          orderForm.sellerCode.trim(),
      orderNo:             orderForm.orderNo.trim(),
      orderDate:           orderForm.orderDate,
      currency:            orderForm.currency,
      fcAmount:            num(orderForm.fcAmount),
      costRate:            num(orderForm.costRate),
      expectedPaymentDate: orderForm.expectedPaymentDate || null,
      bank:                orderForm.bank || null,
      notes:               orderForm.notes || null
    };

    try {
      setSavingOrder(true);

      let orderId;
      if (editingOrder) {
        await axios.put(
          `${CONFIG.API}/api/bills/import-orders/${editingOrder.importOrderId}`,
          payload, auth);
        orderId = editingOrder.importOrderId;
      } else {
        const res = await axios.post(
          `${CONFIG.API}/api/bills/import-orders`, payload, auth);
        orderId = res.data.importOrderId;
      }

      // The schedule is saved separately. It can be refused on its own
      // (once a milestone has been paid), and that must not be reported
      // as the order itself having failed.
      let scheduleNote = '';
      try {
        await axios.post(
          `${CONFIG.API}/api/bills/import-orders/${orderId}/schedule`,
          valid.map(m => ({
            milestoneName: m.milestoneName.trim(),
            fcAmount:      num(m.fcAmount),
            expectedDate:  m.expectedDate || null
          })), auth);
      } catch (err) {
        scheduleNote = ' The order was saved, but the payment schedule was not: '
                     + serverMessage(err, 'the server refused it.');
      }

      say((editingOrder ? 'Order updated.' : 'Order saved.') + scheduleNote,
          scheduleNote ? 'err' : 'ok');

      closeOrderForm();
      await loadOrders();
      await loadSellers();
      if (selected?.importOrderId === orderId) await refreshSelected(orderId);
    } catch (err) {
      say(serverMessage(err, 'The order could not be saved.'), 'err');
    } finally {
      setSavingOrder(false);
    }
  };

  const cancelOrder = async (o) => {
    if (!window.confirm(
      `Cancel import order ${o.orderNo}?\n\n`
    + 'It will be hidden from the list. This cannot be undone from here.'
    )) return;

    try {
      await axios.put(
        `${CONFIG.API}/api/bills/import-orders/${o.importOrderId}/cancel`, {}, auth);
      say(`Order ${o.orderNo} cancelled.`);
      if (selected?.importOrderId === o.importOrderId) setSelected(null);
      await loadOrders();
    } catch (err) {
      say(serverMessage(err, 'The order could not be cancelled.'), 'err');
    }
  };

  // ==================================================================
  // The payment form
  // ==================================================================
  const openPayment = async (milestone) => {
    if (!selected) return;

    const today = new Date().toISOString().substring(0, 10);
    const bank  = selected.bank || '';

    const next = {
      ...emptyPay,
      paymentDate: today,
      bank,
      scheduleId:  milestone ? String(milestone.scheduleId) : '',
      fcAmount:    milestone
                     ? String(milestone.fcOpen ?? milestone.fcAmount ?? '')
                     : String(selected.fcBalance ?? ''),
      spotFc:      milestone
                     ? String(milestone.fcOpen ?? milestone.fcAmount ?? '')
                     : String(selected.fcBalance ?? '')
    };

    setPayForm(next);
    setShowPayForm(true);
    await loadEefcContext(selected.importOrderId, bank, today);
  };

  // Changing the bank or the date changes which ledgers can pay and at
  // what rate, so both reload the context.
  const changePay = (e) => {
    const { name, value } = e.target;

    setPayForm(f => {
      const next = { ...f, [name]: value };

      // Picking a milestone fills in what is still open on it.
      if (name === 'scheduleId') {
        const m = schedule.list.find(x => String(x.scheduleId) === String(value));
        if (m) {
          const open = m.fcOpen ?? m.fcAmount ?? 0;
          next.fcAmount = String(open);
          next.spotFc   = String(Math.max(open - num(f.eefcFc), 0));
        }
      }

      // Changing the bank invalidates the chosen ledger.
      if (name === 'bank') {
        next.eefcAccountId = '';
        next.eefcFc        = '';
      }

      if ((name === 'bank' || name === 'paymentDate') && selected) {
        loadEefcContext(selected.importOrderId, next.bank, next.paymentDate);
      }

      return next;
    });
  };

  const createLedger = async () => {
    if (!eefcContext?.currency || !payForm.bank) return;

    const bankRow = banks.find(b =>
      (b.bankName || '').trim().toUpperCase() === payForm.bank.trim().toUpperCase());

    if (!bankRow) {
      say(`${payForm.bank} is not in the bank list, so a ledger cannot be created `
        + 'for it. Add the bank on the Banks page first.', 'err');
      return;
    }

    if (!window.confirm(
      `Create a ${pairLabel(eefcContext.currency)} EEFC ledger at ${payForm.bank}?\n\n`
    + 'It starts with a zero balance. An opening balance can be added later on the '
    + 'Banks page.'
    )) return;

    try {
      setCreatingLedger(true);
      await axios.post(
        `${CONFIG.API}/api/bills/banks/${bankRow.bankId}/eefc`,
        {
          currency:    eefcContext.currency,
          accountNo:   null,
          openingFc:   0,
          openingRate: 0,
          openingDate: null,
          notes:       'Created from the import orders page'
        }, auth);

      await loadEefcContext(selected.importOrderId, payForm.bank, payForm.paymentDate);
      say('EEFC ledger created.');
    } catch (err) {
      say(serverMessage(err, 'The EEFC ledger could not be created.'), 'err');
    } finally {
      setCreatingLedger(false);
    }
  };

  // ------------------------------------------------------------------
  // The balance rule:   Spot  +  EEFC   =   FC paid
  // ------------------------------------------------------------------
  const calcTotals = () => {
    const fcPaid   = num(payForm.fcAmount);
    const spotFc   = num(payForm.spotFc);
    const spotRate = num(payForm.spotRate);
    const eefcFc   = num(payForm.eefcFc);
    const eefcRate = num(eefcContext?.rate);

    const allocated = spotFc + eefcFc;
    const remaining = fcPaid - allocated;

    const weightedSum = (spotFc * spotRate) + (eefcFc * eefcRate);
    const avgRate     = fcPaid > 0 ? weightedSum / fcPaid : 0;

    const costRate     = num(selected?.costRate);
    // Opposite sign to an export: paying below the budgeted rate is a gain.
    const exchangeDiff = (costRate - avgRate) * fcPaid;

    return { fcPaid, spotFc, spotRate, eefcFc, eefcRate,
             allocated, remaining, weightedSum, avgRate, costRate, exchangeDiff };
  };

  const t = calcTotals();
  const balanced = Math.abs(t.remaining) <= 0.01 && t.fcPaid > 0;

  const chosenLedger = (eefcContext?.accounts || [])
    .find(a => String(a.accountId) === String(payForm.eefcAccountId));

  const savePayment = async () => {
    if (!selected) return;

    if (!payForm.paymentDate)   return say('Enter the payment date.', 'err');
    if (t.fcPaid <= 0)          return say('The amount paid must be more than zero.', 'err');
    if (t.spotFc > 0 && t.spotRate <= 0)
      return say('Enter the spot rate for the amount being bought at spot.', 'err');

    if (t.eefcFc > 0) {
      if (!payForm.bank)
        return say('Choose the bank. EEFC accounts are matched to the paying bank.', 'err');
      if (!payForm.eefcAccountId)
        return say('Choose which EEFC account the currency is paid from.', 'err');
      if (t.eefcRate <= 0)
        return say(eefcContext?.rateReason
          || 'There is no market rate to value the EEFC part with.', 'err');
      if (chosenLedger && t.eefcFc > num(chosenLedger.balanceFc) + 0.005)
        return say(`That ledger holds ${fmt(chosenLedger.balanceFc)}, so `
                 + `${fmt(t.eefcFc)} cannot be paid out of it.`, 'err');
    }

    if (!balanced)
      return say(`The payment is ${fmt(t.fcPaid)} but the parts add up to `
               + `${fmt(t.allocated)}. Spot plus EEFC must equal the amount paid.`, 'err');

    const payload = {
      paymentType:   payForm.paymentType || 'ADVANCE',
      paymentDate:   payForm.paymentDate,
      bank:          payForm.bank || null,
      fcAmount:      t.fcPaid,
      spotFc:        t.spotFc  > 0 ? t.spotFc  : null,
      spotRate:      t.spotFc  > 0 ? t.spotRate : null,
      eefcFc:        t.eefcFc  > 0 ? t.eefcFc  : null,
      eefcAccountId: t.eefcFc  > 0 ? Number(payForm.eefcAccountId) : null,
      scheduleId:    payForm.scheduleId ? Number(payForm.scheduleId) : null,
      notes:         payForm.notes || null
    };

    try {
      setSavingPay(true);
      const res = await axios.post(
        `${CONFIG.API}/api/bills/import-orders/${selected.importOrderId}/payments`,
        payload, auth);

      const d = res.data || {};
      let note = `Payment saved at an average rate of ${fmt(d.weightedAvgRate, 4)}.`;
      if (num(d.eefcFc) > 0)
        note += ` ${fmt(d.eefcFc)} came out of EEFC at ${fmt(d.eefcRate, 4)}.`;

      say(note);
      setShowPayForm(false);
      setPayForm(emptyPay);

      await refreshSelected(selected.importOrderId);

      // The header figures live on the row, so refresh the copy on screen.
      setSelected(s => s ? {
        ...s,
        fcPaid:    d.newFcPaid    ?? s.fcPaid,
        fcBalance: d.newFcBalance ?? s.fcBalance,
        status:    d.newStatus    ?? s.status
      } : s);
    } catch (err) {
      say(serverMessage(err, 'The payment could not be saved.'), 'err');
    } finally {
      setSavingPay(false);
    }
  };

  const undoPayment = async (p) => {
    if (!selected) return;

    if (!p.canUndo) { say(p.undoReason || 'This payment cannot be undone.', 'err'); return; }

    if (!window.confirm(
      `Undo payment ${p.paymentNo} of ${fmt(p.fcAmount)} ${ccyCode(selected.currency)}?\n\n`
    + (num(p.eefcFc) > 0
        ? `${fmt(p.eefcFc)} will go back into the EEFC ledger it came from.\n\n`
        : '')
    + 'The order balance will be worked out again from the payments that remain.'
    )) return;

    try {
      await axios.delete(
        `${CONFIG.API}/api/bills/import-orders/${selected.importOrderId}`
      + `/payments/${p.paymentId}`, auth);

      say(`Payment ${p.paymentNo} undone.`);
      await refreshSelected(selected.importOrderId);

      const fresh = (await axios.get(
        `${CONFIG.API}/api/bills/import-orders/${selected.importOrderId}/payments`,
        auth)).data?.order;

      if (fresh) setSelected(s => s ? {
        ...s, fcPaid: fresh.FcPaid ?? fresh.fcPaid,
              fcBalance: fresh.FcBalance ?? fresh.fcBalance,
              status: fresh.Status ?? fresh.status
      } : s);
    } catch (err) {
      say(serverMessage(err, 'The payment could not be undone.'), 'err');
    }
  };

  // ==================================================================
  // Summary numbers
  // ==================================================================
  const counts = {
    total:    orders.length,
    open:     orders.filter(o => o.status === 'OPEN').length,
    partPaid: orders.filter(o => o.status === 'PART_PAID').length,
    closed:   orders.filter(o => o.status === 'CLOSED').length
  };

  const outstandingFc = orders
    .filter(o => o.status !== 'CANCELLED')
    .reduce((s, o) => s + num(o.fcBalance), 0);

  const pendingMilestones = schedule.list.filter(m => m.status !== 'PAID');

  // Older records may carry a bank name typed in by hand, before the
  // bank master existed. Showing it as "(old)" keeps it selected instead
  // of letting the dropdown silently blank it out on the next save.
  const bankOptions = (current) => {
    const opts = [];
    if (current && !banks.some(b => b.bankName === current)) {
      opts.push(<option key="__old" value={current}>{current} (old)</option>);
    }
    banks.forEach(b => opts.push(
      <option key={b.bankId} value={b.bankName}>
        {b.bankName}{b.branch ? ' — ' + b.branch : ''}
      </option>));
    return opts;
  };

  // ==================================================================
  // Render
  // ==================================================================
  return (
    <div className="content">

      {/* ---------------------------------------------------------- */}
      {/* Summary                                                    */}
      {/* ---------------------------------------------------------- */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Import orders</div>
          <div className="metric-value val-blue">{counts.total}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">{counts.open}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Part paid</div>
          <div className="metric-value val-amber">{counts.partPaid}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Closed</div>
          <div className="metric-value val-blue">{counts.closed}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Still to pay (FC)</div>
          <div className="metric-value val-amber">{fmt0(outstandingFc)}</div>
        </div>
      </div>

      {/* ---------------------------------------------------------- */}
      {/* Header                                                     */}
      {/* ---------------------------------------------------------- */}
      <div className="section-header">
        <div className="section-title">Import orders</div>
        <button className="btn-primary" onClick={() =>
          showOrderForm ? closeOrderForm() : openNewOrder()}>
          {showOrderForm ? '✕ Close' : '+ New import order'}
        </button>
      </div>

      {msg.text && (
        <div style={{
          background:   msg.kind === 'err' ? '#FCE4D6' : '#E2EFDA',
          border:       `1px solid ${msg.kind === 'err' ? '#C00000' : '#70AD47'}`,
          color:        msg.kind === 'err' ? '#7A1F1F' : '#375623',
          borderRadius: '6px', padding: '10px 14px', marginBottom: '12px',
          fontSize: '13px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: '12px'
        }}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg({ text: '', kind: 'ok' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
                     fontSize: '14px', color: 'inherit', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* ---------------------------------------------------------- */}
      {/* The order form                                             */}
      {/* ---------------------------------------------------------- */}
      {showOrderForm && (
        <div className="form-card" style={{ maxWidth: '900px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingOrder ? '✏️ Edit import order' : '📥 New import order'}
          </div>

          {editingOrder && num(editingOrder.fcPaid) > 0 && (
            <div style={{
              background: '#FFF2CC', border: '1px solid #D6B656', color: '#7D6608',
              borderRadius: '6px', padding: '10px 14px', marginBottom: '14px',
              fontSize: '13px'
            }}>
              {fmt(editingOrder.fcPaid)} has already been paid against this order, so
              the server will refuse changes to it. Undo the payments first if it
              really has to be corrected.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Seller *</label>
              <input list="import-sellers" name="sellerCode"
                value={orderForm.sellerCode} onChange={changeOrder}
                placeholder="Supplier name or code" />
              <datalist id="import-sellers">
                {sellers.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div className="form-field">
              <label>Order number *</label>
              <input name="orderNo" value={orderForm.orderNo}
                onChange={changeOrder} placeholder="IMP-2026-001" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Order date *</label>
              <input type="date" name="orderDate"
                value={orderForm.orderDate} onChange={changeOrder} />
            </div>
            <div className="form-field">
              <label>Currency *</label>
              <select name="currency" value={orderForm.currency} onChange={changeOrder}>
                {CURRENCIES.map(c => (
                  <option key={c} value={c}>{pairLabel(c)}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Order amount (FC) *</label>
              <input type="number" name="fcAmount" value={orderForm.fcAmount}
                onChange={changeOrder} placeholder="0.00" />
            </div>
            <div className="form-field">
              <label>Cost rate *</label>
              <input type="number" name="costRate" value={orderForm.costRate}
                onChange={changeOrder} placeholder="0.0000" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Expected payment date</label>
              <input type="date" name="expectedPaymentDate"
                value={orderForm.expectedPaymentDate} onChange={changeOrder} />
            </div>
            <div className="form-field">
              <label>Bank</label>
              <select name="bank" value={orderForm.bank} onChange={changeOrder}>
                <option value="">— Select bank —</option>
                {bankOptions(orderForm.bank)}
              </select>
            </div>
            <div className="form-field">
              <label>Notes</label>
              <input name="notes" value={orderForm.notes}
                onChange={changeOrder} placeholder="Optional" />
            </div>
          </div>

          {num(orderForm.fcAmount) > 0 && num(orderForm.costRate) > 0 && (
            <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '14px' }}>
              Budgeted value:&nbsp;
              <strong style={{ color: '#1F4E79' }}>
                ₹ {fmt0(num(orderForm.fcAmount) * num(orderForm.costRate))}
              </strong>
              &nbsp;— every gain or loss on this order is measured from the cost rate.
            </div>
          )}

          {/* ---- milestones ---------------------------------------- */}
          <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '14px',
                        marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F4E79' }}>
                📅 Payment schedule
                <span style={{ fontWeight: 400, color: '#94A3B8', marginLeft: '6px' }}>
                  (optional)
                </span>
              </div>
              <button className="btn-secondary" onClick={addMilestone}>
                + Add milestone
              </button>
            </div>

            {milestones.map((m, i) => (
              <div key={i} style={{ display: 'grid',
                gridTemplateColumns: '2fr 1.2fr 1.2fr auto',
                gap: '10px', marginBottom: '8px', alignItems: 'end' }}>
                <div className="form-field" style={{ marginBottom: 0 }}>
                  {i === 0 && <label>Milestone</label>}
                  <input value={m.milestoneName}
                    onChange={e => changeMilestone(i, 'milestoneName', e.target.value)}
                    placeholder="e.g. Advance at order" />
                </div>
                <div className="form-field" style={{ marginBottom: 0 }}>
                  {i === 0 && <label>Amount (FC)</label>}
                  <input type="number" value={m.fcAmount}
                    onChange={e => changeMilestone(i, 'fcAmount', e.target.value)}
                    placeholder="0.00" />
                </div>
                <div className="form-field" style={{ marginBottom: 0 }}>
                  {i === 0 && <label>Expected date</label>}
                  <input type="date" value={m.expectedDate}
                    onChange={e => changeMilestone(i, 'expectedDate', e.target.value)} />
                </div>
                <div>
                  {milestones.length > 1 && (
                    <button className="btn-secondary"
                      onClick={() => removeMilestone(i)}
                      style={{ color: '#C00000' }}>✕</button>
                  )}
                </div>
              </div>
            ))}

            {scheduledTotal > 0 && (
              <div style={{ textAlign: 'right', fontSize: '12px',
                            color: '#64748B', marginTop: '8px' }}>
                Scheduled: <strong>{fmt(scheduledTotal)}</strong>
                {num(orderForm.fcAmount) > 0 &&
                 Math.abs(scheduledTotal - num(orderForm.fcAmount)) > 0.01 && (
                  <span style={{ color: scheduledTotal > num(orderForm.fcAmount)
                                   ? '#C00000' : '#7D6608', marginLeft: '10px' }}>
                    {scheduledTotal > num(orderForm.fcAmount)
                      ? `more than the order (${fmt(num(orderForm.fcAmount))})`
                      : `${fmt(num(orderForm.fcAmount) - scheduledTotal)} of the order is not scheduled`}
                  </span>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-primary" onClick={saveOrder} disabled={savingOrder}>
              {savingOrder ? 'Saving…'
                : editingOrder ? '✓ Update order' : '✓ Save order'}
            </button>
            <button className="btn-secondary" onClick={closeOrderForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------- */}
      {/* Filters                                                    */}
      {/* ---------------------------------------------------------- */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center',
                    marginBottom: '12px', flexWrap: 'wrap' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '7px 12px', border: '1px solid #CBD5E1',
                   borderRadius: '6px', fontSize: '14px', color: '#374151' }}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_PAID">Part paid</option>
          <option value="CLOSED">Closed</option>
        </select>

        <select value={filterSeller} onChange={e => setFilterSeller(e.target.value)}
          style={{ padding: '7px 12px', border: '1px solid #CBD5E1',
                   borderRadius: '6px', fontSize: '14px', color: '#374151' }}>
          <option value="">All sellers</option>
          {sellers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <label style={{ fontSize: '13px', color: '#64748B', display: 'flex',
                        alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="checkbox" checked={includeCancelled}
            onChange={e => setIncludeCancelled(e.target.checked)} />
          Show cancelled
        </label>

        {(filterStatus || filterSeller || includeCancelled) && (
          <button className="btn-secondary" onClick={() => {
            setFilterStatus(''); setFilterSeller(''); setIncludeCancelled(false);
          }}>✕ Clear filters</button>
        )}

        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          {orders.length} order{orders.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* ---------------------------------------------------------- */}
      {/* Orders table                                               */}
      {/* ---------------------------------------------------------- */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading…</p>
        ) : orders.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No import orders found.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Order no</th>
                <th>Seller</th>
                <th>Date</th>
                <th>Currency</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Cost rate</th>
                <th>Due</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th>Bank</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const isOpen = selected?.importOrderId === o.importOrderId;
                return (
                  <tr key={o.importOrderId}
                    style={isOpen ? { background: '#DEEAF1' } : undefined}>
                    <td><strong>{o.orderNo}</strong></td>
                    <td>{o.sellerCode}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{o.orderDate || '—'}</td>
                    <td>{pairLabel(o.currency)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(o.fcAmount)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(o.costRate, 4)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {o.expectedPaymentDate || '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: '#375623' }}>
                      {fmt(o.fcPaid)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600,
                      color: num(o.fcBalance) > 0.01 ? '#C00000' : '#375623' }}>
                      {fmt(o.fcBalance)}
                    </td>
                    <td>{o.bank || '—'}</td>
                    <td>{pill(statusPill(o.status), o.status)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-secondary"
                        onClick={() => openOrder(o)}
                        style={{ marginRight: '6px' }}>
                        {isOpen ? '▲ Hide' : '▼ Open'}
                      </button>
                      {o.status !== 'CANCELLED' && num(o.fcPaid) <= 0.01 && (
                        <>
                          <button className="btn-secondary"
                            onClick={() => openEditOrder(o)}
                            style={{ marginRight: '6px' }}>Edit</button>
                          <button className="btn-secondary"
                            onClick={() => cancelOrder(o)}
                            style={{ color: '#C00000' }}>Cancel</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------------------------------------------------------- */}
      {/* The open order                                             */}
      {/* ---------------------------------------------------------- */}
      {selected && (
        <div style={{ border: '1px solid #BCD4E6', borderRadius: '8px',
          padding: '16px', marginTop: '20px', background: '#F0F7FF' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', marginBottom: '14px', gap: '16px',
            flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#1F4E79' }}>
                {selected.orderNo} — {selected.sellerCode}
              </div>
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                {pairLabel(selected.currency)} &nbsp;|&nbsp;
                Order <strong>{fmt(selected.fcAmount)}</strong> &nbsp;|&nbsp;
                Paid <strong style={{ color: '#375623' }}>{fmt(selected.fcPaid)}</strong>
                &nbsp;|&nbsp;
                Balance <strong style={{ color: num(selected.fcBalance) > 0.01
                  ? '#C00000' : '#375623' }}>{fmt(selected.fcBalance)}</strong>
                &nbsp;|&nbsp;
                Cost rate <strong>{fmt(selected.costRate, 4)}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {selected.status !== 'CANCELLED' &&
               num(selected.fcBalance) > 0.01 && !showPayForm && (
                <button className="btn-primary" onClick={() => openPayment(null)}>
                  💳 Make a payment
                </button>
              )}
              <button className="btn-secondary"
                onClick={() => { setSelected(null); setShowPayForm(false); }}>
                ✕ Close
              </button>
            </div>
          </div>

          {/* ---- schedule ---------------------------------------- */}
          <div style={{ marginBottom: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1F4E79',
                          marginBottom: '8px' }}>
              📅 Payment schedule
            </div>

            {schedule.list.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#64748B', background: '#fff',
                padding: '12px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                No payment schedule on this order. Payments can still be made against
                the order as a whole.
              </div>
            ) : (
              <div className="table-wrap" style={{ background: '#fff' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Milestone</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'right' }}>Paid</th>
                      <th style={{ textAlign: 'right' }}>Still open</th>
                      <th>Expected</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.list.map((m, i) => (
                      <tr key={m.scheduleId}>
                        <td style={{ color: '#94A3B8' }}>{i + 1}</td>
                        <td><strong>{m.milestoneName}</strong></td>
                        <td style={{ textAlign: 'right' }}>{fmt(m.fcAmount)}</td>
                        <td style={{ textAlign: 'right', color: '#375623' }}>
                          {fmt(m.fcPaid)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(m.fcOpen)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{m.expectedDate || '—'}</td>
                        <td>{pill(milestonePill(m.status), m.status)}</td>
                        <td>
                          {m.status !== 'PAID' &&
                           selected.status !== 'CANCELLED' &&
                           num(selected.fcBalance) > 0.01 && (
                            <button className="btn-secondary"
                              onClick={() => openPayment(m)}>
                              💳 Pay this
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#F1F5F9', fontWeight: 600 }}>
                      <td colSpan={2}>Scheduled total</td>
                      <td style={{ textAlign: 'right' }}>
                        {fmt(schedule.list.reduce((s, m) => s + num(m.fcAmount), 0))}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {fmt(schedule.list.reduce((s, m) => s + num(m.fcPaid), 0))}
                      </td>
                      <td colSpan={4}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* The order balance and the milestone totals can differ
                legitimately, most often because PCFC paid the order
                directly without touching any milestone. Saying so is
                better than letting the two numbers quietly disagree. */}
            {schedule.list.length > 0 && (() => {
              const milestonePaid = schedule.list.reduce((s, m) => s + num(m.fcPaid), 0);
              const orderPaid     = num(selected.fcPaid);
              const gap           = orderPaid - milestonePaid;
              if (Math.abs(gap) <= 0.01) return null;

              return (
                <div style={{ fontSize: '12px', color: '#7D6608', background: '#FFF2CC',
                  border: '1px solid #D6B656', borderRadius: '6px',
                  padding: '10px 12px', marginTop: '8px' }}>
                  {fmt(gap)} has been paid against this order without being attached to
                  any milestone. That is normal when a PCFC loan paid the bill directly,
                  since PCFC pays the order rather than a milestone.
                </div>
              );
            })()}
          </div>

          {/* ---- payment form ------------------------------------- */}
          {showPayForm && (
            <div className="form-card" style={{ background: '#fff',
              border: '2px solid #1F4E79', marginBottom: '18px' }}>

              <div className="form-step" style={{ marginBottom: '14px' }}>
                💳 Payment against {selected.orderNo}
                {payForm.scheduleId && schedule.list.find(
                  m => String(m.scheduleId) === String(payForm.scheduleId)) && (
                  <span style={{ fontWeight: 400, color: '#64748B', marginLeft: '8px' }}>
                    — {schedule.list.find(
                        m => String(m.scheduleId) === String(payForm.scheduleId)).milestoneName}
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                            gap: '12px', marginBottom: '12px' }}>
                <div className="form-field">
                  <label>Payment date *</label>
                  <input type="date" name="paymentDate"
                    value={payForm.paymentDate} onChange={changePay} />
                </div>
                <div className="form-field">
                  <label>Type</label>
                  <select name="paymentType" value={payForm.paymentType}
                    onChange={changePay}>
                    {PAYMENT_TYPES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Bank</label>
                  <select name="bank" value={payForm.bank} onChange={changePay}>
                    <option value="">— Select bank —</option>
                    {bankOptions(payForm.bank)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Against milestone</label>
                  <select name="scheduleId" value={payForm.scheduleId}
                    onChange={changePay}>
                    <option value="">— The order as a whole —</option>
                    {pendingMilestones.map(m => (
                      <option key={m.scheduleId} value={m.scheduleId}>
                        {m.milestoneName} — {fmt(m.fcOpen)} open
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-field" style={{ maxWidth: '260px' }}>
                <label>Total paid (FC) *</label>
                <input type="number" name="fcAmount"
                  value={payForm.fcAmount} onChange={changePay} placeholder="0.00" />
              </div>

              <div style={{ fontSize: '12px', color: '#64748B',
                            margin: '6px 0 16px' }}>
                Still owed on this order: <strong>{fmt(selected.fcBalance)}</strong>
                &nbsp; {pairLabel(selected.currency)}
              </div>

              {/* ---- spot ------------------------------------------ */}
              <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '14px',
                            marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F4E79',
                              marginBottom: '10px' }}>
                  💱 Bought at spot
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                              gap: '12px' }}>
                  <div className="form-field">
                    <label>Amount (FC)</label>
                    <input type="number" name="spotFc"
                      value={payForm.spotFc} onChange={changePay} placeholder="0.00" />
                  </div>
                  <div className="form-field">
                    <label>Rate</label>
                    <input type="number" name="spotRate"
                      value={payForm.spotRate} onChange={changePay}
                      placeholder="0.0000" />
                  </div>
                  <div className="form-field">
                    <label>Rupees</label>
                    <input readOnly value={t.spotFc > 0 && t.spotRate > 0
                      ? fmt0(t.spotFc * t.spotRate) : '—'}
                      style={{ background: '#F1F5F9', fontWeight: 600 }} />
                  </div>
                </div>
              </div>

              {/* ---- EEFC ------------------------------------------ */}
              <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '14px',
                            marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F4E79',
                              marginBottom: '4px' }}>
                  🏦 Paid out of EEFC
                </div>
                <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '10px' }}>
                  {pairLabel(selected.currency)} held at the bank pays this supplier
                  directly. No currency is sold, so no spread is paid.
                </div>

                {!payForm.bank ? (
                  <div style={{ fontSize: '12px', color: '#7D6608',
                    background: '#FFF2CC', border: '1px solid #D6B656',
                    borderRadius: '6px', padding: '10px 12px' }}>
                    Choose the bank above. EEFC accounts are matched to the paying bank.
                  </div>
                ) : eefcContext?.accounts?.length ? (
                  <>
                    <div style={{ display: 'grid',
                      gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: '12px' }}>
                      <div className="form-field">
                        <label>EEFC account</label>
                        <select name="eefcAccountId" value={payForm.eefcAccountId}
                          onChange={changePay}>
                          <option value="">— None —</option>
                          {eefcContext.accounts.map(a => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.bankName} {pairLabel(a.currency)}
                              {a.accountNo ? ` (${a.accountNo})` : ''}
                              {' — '}{fmt(a.balanceFc)} available
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Amount (FC)</label>
                        <input type="number" name="eefcFc"
                          value={payForm.eefcFc} onChange={changePay}
                          placeholder="0.00"
                          disabled={!payForm.eefcAccountId} />
                      </div>
                      <div className="form-field">
                        <label>Valued at</label>
                        <input readOnly
                          value={t.eefcRate > 0 ? fmt(t.eefcRate, 4) : '—'}
                          style={{ background: '#F1F5F9', fontWeight: 600 }} />
                      </div>
                      <div className="form-field">
                        <label>Rupees</label>
                        <input readOnly value={t.eefcFc > 0 && t.eefcRate > 0
                          ? fmt0(t.eefcFc * t.eefcRate) : '—'}
                          style={{ background: '#F1F5F9', fontWeight: 600 }} />
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                      {t.eefcRate > 0
                        ? `Valued at the ${pairLabel(selected.currency)} close of `
                          + `${fmt(t.eefcRate, 4)} on ${eefcContext.rateDate}. No rupees `
                          + 'change hands — the rate is only used to record the movement.'
                        : (eefcContext.rateReason || 'No market rate is available yet.')}
                    </div>

                    {chosenLedger && t.eefcFc > num(chosenLedger.balanceFc) + 0.005 && (
                      <div style={{ fontSize: '12px', color: '#C00000',
                        background: '#FCE4D6', border: '1px solid #E6B0AA',
                        borderRadius: '6px', padding: '10px 12px', marginTop: '8px' }}>
                        That ledger holds {fmt(chosenLedger.balanceFc)}, so{' '}
                        {fmt(t.eefcFc)} cannot be paid out of it.
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#7D6608',
                    background: '#FFF2CC', border: '1px solid #D6B656',
                    borderRadius: '6px', padding: '10px 12px',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span>{eefcContext?.reason
                      || 'No EEFC account is available for this bank and currency.'}</span>
                    {eefcContext?.accounts?.length === 0 && eefcContext?.currency && (
                      <button className="btn-secondary" onClick={createLedger}
                        disabled={creatingLedger}>
                        {creatingLedger ? 'Creating…' : '+ Create the ledger'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ---- balance bar ------------------------------------ */}
              <div style={{
                background: balanced ? '#E2EFDA' : '#FFF2CC',
                border: `1px solid ${balanced ? '#70AD47' : '#D6B656'}`,
                borderRadius: '6px', padding: '12px 14px', marginBottom: '14px'
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px',
                              fontSize: '13px', alignItems: 'center' }}>
                  <span>Spot <strong>{fmt(t.spotFc)}</strong></span>
                  <span>+</span>
                  <span>EEFC <strong>{fmt(t.eefcFc)}</strong></span>
                  <span>=</span>
                  <span><strong>{fmt(t.allocated)}</strong></span>
                  <span style={{ color: '#64748B' }}>
                    of {fmt(t.fcPaid)} paid
                  </span>
                  <span style={{ marginLeft: 'auto', fontWeight: 600,
                    color: balanced ? '#375623' : '#7D6608' }}>
                    {balanced
                      ? '✓ Balanced'
                      : t.remaining > 0
                        ? `${fmt(t.remaining)} not yet accounted for`
                        : `${fmt(Math.abs(t.remaining))} more than the payment`}
                  </span>
                </div>

                {balanced && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px',
                    fontSize: '12px', color: '#475569', marginTop: '10px',
                    paddingTop: '10px', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <span>Average rate <strong>{fmt(t.avgRate, 4)}</strong></span>
                    <span>Rupees <strong>₹ {fmt0(t.weightedSum)}</strong></span>
                    <span>Cost rate <strong>{fmt(t.costRate, 4)}</strong></span>
                    <span>
                      Exchange difference{' '}
                      <strong style={{ color: t.exchangeDiff >= 0 ? '#375623' : '#C00000' }}>
                        ₹ {fmt0(t.exchangeDiff)}
                      </strong>
                      <span style={{ color: '#94A3B8', marginLeft: '6px' }}>
                        ({t.exchangeDiff >= 0 ? 'paid below cost rate' : 'paid above cost rate'})
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div className="form-field" style={{ maxWidth: '420px' }}>
                <label>Notes</label>
                <input name="notes" value={payForm.notes}
                  onChange={changePay} placeholder="Optional" />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                <button className="btn-primary" onClick={savePayment}
                  disabled={savingPay || !balanced}>
                  {savingPay ? 'Saving…' : '✓ Save payment'}
                </button>
                <button className="btn-secondary"
                  onClick={() => { setShowPayForm(false); setPayForm(emptyPay); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ---- payments made ----------------------------------- */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1F4E79',
                          marginBottom: '8px' }}>
              💳 Payments made
            </div>

            {payments.list.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#64748B', background: '#fff',
                padding: '12px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                No payments recorded against this order yet.
              </div>
            ) : (
              <div className="table-wrap" style={{ background: '#fff' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Milestone</th>
                      <th style={{ textAlign: 'right' }}>Paid</th>
                      <th style={{ textAlign: 'right' }}>Spot</th>
                      <th style={{ textAlign: 'right' }}>EEFC</th>
                      <th style={{ textAlign: 'right' }}>Avg rate</th>
                      <th style={{ textAlign: 'right' }}>Rupees</th>
                      <th style={{ textAlign: 'right' }}>Exch diff</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.list.map(p => (
                      <tr key={p.paymentId}>
                        <td style={{ color: '#94A3B8' }}>{p.paymentNo}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{p.paymentDate}</td>
                        <td>
                          {p.fromPcfc
                            ? pill({ background: '#E7E6F5', color: '#4B3F9E' }, 'via PCFC')
                            : (PAYMENT_TYPES.find(x => x.value === p.paymentType)?.label
                               || p.paymentType)}
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748B' }}>
                          {p.milestoneName || '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(p.fcAmount)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {num(p.spotFc) > 0 ? fmt(p.spotFc) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: '#1F4E79' }}>
                          {num(p.eefcFc) > 0 ? fmt(p.eefcFc) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {fmt(p.weightedAvgRate, 4)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {fmt0(p.inrAmount)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600,
                          color: num(p.exchangeDiffInr) >= 0 ? '#375623' : '#C00000' }}>
                          {fmt0(p.exchangeDiffInr)}
                        </td>
                        <td>
                          <button className="btn-secondary"
                            onClick={() => undoPayment(p)}
                            title={p.undoReason || 'Undo this payment'}
                            style={{ color: p.canUndo ? '#C00000' : '#94A3B8',
                                     cursor: p.canUndo ? 'pointer' : 'not-allowed' }}>
                            ↩ Undo
                          </button>
                        </td>
                      </tr>
                    ))}

                    {payments.totals && (
                      <tr style={{ background: '#F1F5F9', fontWeight: 600 }}>
                        <td colSpan={4}>Total</td>
                        <td style={{ textAlign: 'right' }}>
                          {fmt(payments.totals.fcPaid)}
                        </td>
                        <td colSpan={2}></td>
                        <td style={{ textAlign: 'right' }}>
                          {fmt(payments.totals.weightedAvgRate, 4)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {fmt0(payments.totals.inrPaid)}
                        </td>
                        <td style={{ textAlign: 'right',
                          color: num(payments.totals.exchangeDiffInr) >= 0
                            ? '#375623' : '#C00000' }}>
                          {fmt0(payments.totals.exchangeDiffInr)}
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportOrdersPage;
